import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { OAuthProvider } from '@pepperhq/location-sdk';
import { Permission, Resource } from '@pepperhq/auth-client';
import { useSnackbar } from 'notistack';

import { useAppSelector } from 'store/hooks';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { MESSAGE_INTEGRATION_OAUTH_CONNECT_ERROR } from 'config/messages';
import { USER_MERCHANTS } from 'config/routes';
import { Throbber } from 'ui/Throbber';

const isOAuthProvider = (value?: string): value is OAuthProvider =>
    !!value && (Object.values(OAuthProvider) as string[]).includes(value);

const PROVIDER_RESOURCE: Record<OAuthProvider, Resource> = {
    [OAuthProvider.SQUARE]: Resource.SquarePrivateSetting,
    [OAuthProvider.SHOPIFY]: Resource.ShopifyPrivateSetting,
    [OAuthProvider.LIGHTSPEED]: Resource.LightspeedPrivateSetting,
    [OAuthProvider.LIGHTSPEED_K_SERIES]: Resource.LightspeedKSeriesPrivateSetting,
    [OAuthProvider.KLAVIYO]: Resource.KlaviyoPrivateSetting
};

// Utility route:
//  - once authorised, fetch the provider's install URL and forward the browser to it.
//  - Renders only a loading throbber; unauthenticated users are sent to sign-in by PrivateRoute first.
const OAuthInstallPage: React.FC = () => {
    const { provider } = useParams<{ provider: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const claims = useAppSelector(state => state.auth.claims);

    React.useEffect(() => {
        const forwardToProvider = async () => {
            if (!isOAuthProvider(provider)) {
                enqueueSnackbar(MESSAGE_INTEGRATION_OAUTH_CONNECT_ERROR(provider ?? 'integration'), {
                    variant: 'error'
                });
                navigate(USER_MERCHANTS, { replace: true });
                return;
            }
            // A merchant must be selected before the token has permission to request the install URL.
            // Send the user to pick one, then come back here once the tenant-scoped token is issued.
            if (!claims?.tenantId) {
                const redirectTo = encodeURIComponent(location.pathname);
                navigate(`${USER_MERCHANTS}?redirectTo=${redirectTo}`, { replace: true });
                return;
            }
            // Permissions are tenant-scoped, so this check only runs once a merchant is selected.
            if (!claims.hasPermission(PROVIDER_RESOURCE[provider], Permission.write)) {
                enqueueSnackbar(MESSAGE_INTEGRATION_OAUTH_CONNECT_ERROR(provider), { variant: 'error' });
                navigate(USER_MERCHANTS, { replace: true });
                return;
            }
            try {
                // Authenticated request (bearer attached by the SDK); the install URL is built server-side.
                const { url } = await new LocationSettingsApi().getOAuthInstallUrl(provider);
                window.location.href = url;
            } catch {
                enqueueSnackbar(MESSAGE_INTEGRATION_OAUTH_CONNECT_ERROR(provider), { variant: 'error' });
                navigate(USER_MERCHANTS, { replace: true });
            }
        };
        forwardToProvider();
    }, [provider, claims, location.pathname, navigate, enqueueSnackbar]);

    return <Throbber />;
};

export { OAuthInstallPage };
